import { Component, EventEmitter, Input, Output } from "@angular/core";
import { ClippingStudioTextTemplate } from "app/js/src/domain/clipping-studio/clipping-studio-text-template";

const CARD_TITLE_MAX = 10;
const CARD_FONT_SIZE_MAX_PX = 28;
/** Relative luminance above this → treat text as light (use dark checkerboard). */
const LIGHT_TEXT_LUMINANCE_THRESHOLD = 0.6;

@Component({
  selector: "evrideo-clipping-studio-text-template-card",
  templateUrl: "./clipping-studio-text-template-card.component.html",
  styleUrls: ["./clipping-studio-text-template-card.component.scss"],
})
export class ClippingStudioTextTemplateCardComponent {
  @Input() template!: ClippingStudioTextTemplate;
  @Input() isEditMode = true;
  @Output() add = new EventEmitter<ClippingStudioTextTemplate>();
  /** Add text and open the styling editor (Edit overlay action). */
  @Output() edit = new EventEmitter<ClippingStudioTextTemplate>();
  @Output() delete = new EventEmitter<ClippingStudioTextTemplate>();

  get cardTitle(): string {
    const text = (this.template?.text ?? "").trim() || "Text";
    if (text.length <= CARD_TITLE_MAX) return text;
    return `${text.slice(0, CARD_TITLE_MAX)}…`;
  }

  /** True when template has no solid fill — show transparency checkerboard. */
  get isTransparentPreview(): boolean {
    const bg = this.template?.backgroundColor;
    return bg == null || bg === "" || bg === "transparent";
  }

  /**
   * Checkerboard tone for transparent previews: dark board for light text, light board for dark text.
   */
  get checkerboardTone(): "light" | "dark" {
    return this.isLightTextColor(this.previewTextColor) ? "dark" : "light";
  }

  get previewBackground(): string | null {
    if (this.isTransparentPreview) return null;
    return this.template.backgroundColor;
  }

  get previewTextColor(): string {
    return this.template?.fontColor || "#ffffff";
  }

  get previewFontSize(): string {
    const size = this.template?.fontSize ?? 18;
    return `${Math.min(Math.max(size, 12), CARD_FONT_SIZE_MAX_PX)}px`;
  }

  get previewFontWeight(): string {
    return this.template?.fontWeight || "400";
  }

  get previewFontStyle(): string {
    return this.template?.fontStyle || "normal";
  }

  get previewTextShadow(): string {
    if (!this.template?.dropShadow) return "none";
    const color = this.template.shadowColor || "rgba(0,0,0,0.55)";
    const blur = this.template.shadowBlur ?? 4;
    const ox = this.template.shadowOffsetX ?? 0;
    const oy = this.template.shadowOffsetY ?? 2;
    return `${ox}px ${oy}px ${blur}px ${color}`;
  }

  get showBadge(): boolean {
    return !!this.template?.badge && this.template.badge.trim().length > 0;
  }

  onAdd(): void {
    this.add.emit(this.template);
  }

  onEdit(): void {
    this.edit.emit(this.template);
  }

  onDelete(): void {
    this.delete.emit(this.template);
  }

  private isLightTextColor(color: string): boolean {
    const rgb = parseCssColorToRgb(color);
    if (!rgb) {
      // Unknown format: assume light (default card text is white).
      return true;
    }
    const [r, g, b] = rgb;
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance >= LIGHT_TEXT_LUMINANCE_THRESHOLD;
  }
}

function parseCssColorToRgb(
  color: string,
): [number, number, number] | null {
  const value = (color || "").trim().toLowerCase();
  if (!value) return null;

  const hex = value.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hex) {
    const h = hex[1];
    if (h.length === 3) {
      return [
        parseInt(h[0] + h[0], 16),
        parseInt(h[1] + h[1], 16),
        parseInt(h[2] + h[2], 16),
      ];
    }
    return [
      parseInt(h.slice(0, 2), 16),
      parseInt(h.slice(2, 4), 16),
      parseInt(h.slice(4, 6), 16),
    ];
  }

  const rgb = value.match(
    /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*[\d.]+\s*)?\)$/,
  );
  if (rgb) {
    return [
      clampByte(Number(rgb[1])),
      clampByte(Number(rgb[2])),
      clampByte(Number(rgb[3])),
    ];
  }

  return null;
}

function clampByte(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(255, n));
}
